import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useState } from 'react';
import { Button } from '../../../components/Button';
import { savePdf } from '../../../hooks/useDownloadInvoice';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { T, useT } from '../../../translation/src';
import { ReferralOverviewFieldsFragment, useOrganisationReferralPayoutPdfLazyQuery } from '../../../types/graphqlTypes';
import { formatPrice } from '../../../utils/formatPrice';

interface PayoutHistoryProps {
  organisationId: string;
  payouts: ReferralOverviewFieldsFragment['payouts'];
}
// A partner's payouts: only the ones Pointorama paid, each with its bill to download.
const PayoutHistory_: React.FC<PayoutHistoryProps> = ({ organisationId, payouts }) => {
  const title = useT('payout history', { swc: true });
  const failed = useT('could not download the bill', { swc: true });
  const { formatDay } = useFormatDate();
  const { showToast } = useToastNotifications();
  const [getPdf] = useOrganisationReferralPayoutPdfLazyQuery({ fetchPolicy: 'no-cache' });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const onDownload = useCallback(
    async (payoutId: string) => {
      setDownloadingId(payoutId);
      try {
        const { data, error } = await getPdf({ variables: { organisationId, payoutId } });
        if (!data) throw error ?? new Error('NO_DATA');
        savePdf(data.organisationReferralPayoutPdf);
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      } finally {
        setDownloadingId(null);
      }
    },
    [failed, getPdf, organisationId, showToast],
  );

  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#242424]">
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <span className="text-base font-semibold">{title}</span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">{payouts.length}</span>
      </div>
      {payouts.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-gray-500">
          <T _str="No payout has been paid yet. Once one is, it shows up here with its bill." />
        </div>
      ) : (
        <div className="flex max-h-[24rem] flex-col divide-y divide-gray-100 overflow-y-auto dark:divide-gray-700">
          {payouts.map((payout) => (
            <div key={payout.id} className="flex items-center gap-4 px-6 py-3 text-sm">
              <div className="flex min-w-0 grow flex-col">
                <span className="font-semibold">{payout.manual ? formatDay(payout.periodEnd) : `${formatDay(payout.periodStart)} – ${formatDay(new Date(new Date(payout.periodEnd).getTime() - 1))}`}</span>
                <span className="text-xs text-gray-500">
                  <T _str="{count} sales" args={{ count: payout.saleCount }} />
                  {payout.paidAt && (
                    <>
                      {' · '}
                      <T _str="paid" swc /> {formatDay(payout.paidAt)}
                    </>
                  )}
                </span>
              </div>
              <span className="w-28 shrink-0 text-right font-semibold">{formatPrice(payout.totalCents)}</span>
              <Button variant="secondary" size="xs" onClick={() => onDownload(payout.id)} loading={downloadingId === payout.id} className="shrink-0">
                <ArrowDownTrayIcon className="mr-1 h-4 w-4" />
                <T _str="bill" swc />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export const PayoutHistory = memo(PayoutHistory_);
